import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { RateViewerView } from './views/rate-viewer-view';
import { Providers } from '../../enums/providers';
import { selectRateViewerIsLoading } from '../../store/rate-viewer/rate-viewer.selectors';

const getProgressForCarriersView = (filteredRateGroups, isRateViewerLoading) => {
  const rateProvidersRequestedCount = filteredRateGroups.length;

  return {
    shouldShowProgress: rateProvidersRequestedCount > 0,
    shouldShowLoadingIndicator: isRateViewerLoading,
  };
};

const getLoadingStatuses = (filteredRateGroups) => {
  const loadingStatuses = {
    isLoadingUpsWalletRates: false,
    hasUpsWalletRates: false,
    isLoadingUspsWalletRates: false,
    hasUspsWalletRates: false,
    isLoadingOtherRates: false,
    hasOtherRates: false,
  };

  filteredRateGroups.forEach(({ labelProviderId, walletId, isLoading, rates }) => {
    if (labelProviderId === Providers.UPSWallet.labelProviderId && walletId) {
      if (isLoading) {
        loadingStatuses.isLoadingUpsWalletRates = true;
      } else if (rates.length > 0) {
        loadingStatuses.hasUpsWalletRates = true;
      }
    } else if (labelProviderId === Providers.Stamps.labelProviderId && walletId) {
      if (isLoading) {
        loadingStatuses.isLoadingUspsWalletRates = true;
      } else if (rates.length > 0) {
        loadingStatuses.hasUspsWalletRates = true;
      }
    } else if (isLoading) {
      loadingStatuses.isLoadingOtherRates = true;
    } else if (rates.length > 0) {
      loadingStatuses.hasOtherRates = true;
    }
  });

  return loadingStatuses;
};

// Waits for all UPS Wallet and USPS Wallet accounts to finish loading before showing progress
const getProgressForAllRatesView = (filteredRateGroups) => {
  const { isLoadingUpsWalletRates, hasUpsWalletRates, isLoadingUspsWalletRates, hasUspsWalletRates, isLoadingOtherRates, hasOtherRates } =
    getLoadingStatuses(filteredRateGroups);

  const hasUspsAndUpsFinishedLoading = !isLoadingUpsWalletRates && !isLoadingUspsWalletRates;
  if (!hasUspsAndUpsFinishedLoading) {
    return {
      shouldShowProgress: false,
      shouldShowLoadingIndicator: true,
    };
  }

  if (hasUpsWalletRates || hasUspsWalletRates) {
    return {
      shouldShowProgress: true,
      shouldShowLoadingIndicator: false,
    };
  }

  return {
    shouldShowProgress: hasOtherRates,
    shouldShowLoadingIndicator: isLoadingOtherRates && !hasOtherRates,
  };
};

export const useRateViewerProgress = (rateViewerKey, filteredRateGroups, selectedView) => {
  const isRateViewerLoading = useSelector((state) => selectRateViewerIsLoading(state, { rateViewerKey }));

  return useMemo(() => {
    if (filteredRateGroups.length === 0) {
      return {
        shouldShowProgress: false,
        shouldShowLoadingIndicator: isRateViewerLoading,
        rateProvidersLoadedCount: 0,
        rateProvidersRequestedCount: 0,
      };
    }

    let progressForView;
    switch (selectedView) {
      case RateViewerView.AllRates:
        progressForView = getProgressForAllRatesView(filteredRateGroups);
        break;
      case RateViewerView.Carriers:
      default:
        progressForView = getProgressForCarriersView(filteredRateGroups, isRateViewerLoading);
    }

    return {
      ...progressForView,
      rateProvidersLoadedCount: filteredRateGroups.filter(({ isLoading }) => !isLoading).length,
      rateProvidersRequestedCount: filteredRateGroups.length,
    };
  }, [filteredRateGroups, selectedView, isRateViewerLoading]);
};
